import React, { Component } from "react";
import { Link } from "react-router-dom";
import {
  NAV_PRODUCT_CATEGORIES,
  NAV_PRODUCT_PLACEHOLDER_IMAGE,
  resolveNavProductImageUrl,
} from "../../data/navProductsData";

const CATEGORY_HEADER_STYLE = {
  pointerEvents: "none",
  textDecoration: "none",
  color: "inherit",
  display: "block",
  cursor: "default",
};

const MEGA_MENU_STYLE = {
  minWidth: "1000px",
  maxWidth: "min(1000px, 95vw)",
  display: "flex",
  flexWrap: "nowrap",
  left: "50%",
  transform: "translateX(-50%)",
  right: "auto",
};

const COLUMN_STYLE = { flex: "1 1 25%", minWidth: "200px" };

/**
 * Products navigation: original mega-menu dropdown + separate hover flyout.
 *
 * Hover behavior:
 * - Products dropdown uses the original theme mega-menu (unchanged width/layout).
 * - Hovering a product link opens a flyout positioned to the right of the dropdown.
 * - Flyout is a sibling panel (not inside the dropdown), with a left bridge for cursor travel.
 * - Leaving the dropdown and flyout closes the preview.
 */
class ProductsMegaMenu extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hoveredProduct: null,
      previewImageSrc: null,
      flyoutTop: 0,
      flyoutLeft: 0,
    };
    this.productsNavRef = React.createRef();
    this.megaMenuRef = React.createRef();
    this.flyoutRef = React.createRef();
    this.hoveredLinkEl = null;
  }

  componentDidMount() {
    this._onLayoutChange = () => {
      if (this.state.hoveredProduct) {
        this.updateFlyoutPosition();
      }
    };
    window.addEventListener("resize", this._onLayoutChange);
    window.addEventListener("scroll", this._onLayoutChange, true);
  }

  componentWillUnmount() {
    window.removeEventListener("resize", this._onLayoutChange);
    window.removeEventListener("scroll", this._onLayoutChange, true);
  }

  getPublicUrl = () => {
    return this.props.publicUrl || process.env.PUBLIC_URL + "/";
  };

  /**
   * Positions the flyout beside the dropdown, vertically aligned with the
   * hovered product row (slightly below the link top), like the reference UI.
   */
  updateFlyoutPosition = () => {
    if (!this.megaMenuRef.current) {
      return;
    }

    const menuRect = this.megaMenuRef.current.getBoundingClientRect();
    const linkRect = this.hoveredLinkEl
      ? this.hoveredLinkEl.getBoundingClientRect()
      : menuRect;

    const flyoutEl = this.flyoutRef.current;
    const flyoutHeight = (flyoutEl && flyoutEl.offsetHeight) || 220;
    const flyoutWidth = (flyoutEl && flyoutEl.offsetWidth) || 220;
    const gap = 8;

    // Beside the hovered row, slightly below — matches reference screenshot
    let top = linkRect.top + 4;
    let left = linkRect.right + gap;

    const maxTop = window.innerHeight - flyoutHeight - 12;
    if (top > maxTop) {
      top = maxTop;
    }
    if (top < 12) {
      top = 12;
    }
    if (left + flyoutWidth > window.innerWidth - 12) {
      left = Math.max(12, window.innerWidth - flyoutWidth - 12);
    }

    this.setState({
      flyoutTop: top,
      flyoutLeft: left,
    });
  };

  /**
   * Opens or updates the flyout for the hovered product link.
   */
  handleProductHover = (product, event) => {
    this.hoveredLinkEl = event.currentTarget;
    this.setState(
      {
        hoveredProduct: product,
        previewImageSrc: resolveNavProductImageUrl(
          this.getPublicUrl(),
          product.image
        ),
      },
      () => {
        this.updateFlyoutPosition();
        requestAnimationFrame(() => this.updateFlyoutPosition());
      }
    );
  };

  clearFlyout = () => {
    this.hoveredLinkEl = null;
    this.setState({
      hoveredProduct: null,
      previewImageSrc: null,
    });
  };

  /** Fallback to placeholder if product image is missing or fails to load. */
  handlePreviewImageError = () => {
    const fallback = resolveNavProductImageUrl(
      this.getPublicUrl(),
      NAV_PRODUCT_PLACEHOLDER_IMAGE
    );
    if (this.state.previewImageSrc !== fallback) {
      this.setState({ previewImageSrc: fallback });
    }
  };

  // isWithinNode = (node, target) => {
  //   return node && target && (node === target || node.contains(target));
  // };
  isWithinNode = (node, target) => {
  if (!(node instanceof Node)) {
    return false;
  }

  if (!(target instanceof Node)) {
    return false;
  }

  return node === target || node.contains(target);
};

  /** Closes flyout when the pointer leaves the entire Products nav item. */
  handleProductsAreaLeave = (event) => {
    if (this.isWithinNode(this.productsNavRef.current, event.relatedTarget)) {
      return;
    }
    this.clearFlyout();
  };

  /**
   * Closes flyout when leaving the dropdown, unless the cursor enters the flyout.
   */
  handleMegaMenuLeave = (event) => {
    if (this.isWithinNode(this.flyoutRef.current, event.relatedTarget)) {
      return;
    }
    this.clearFlyout();
  };

  /**
   * Closes flyout when leaving the flyout, unless the cursor returns to the dropdown.
   */
  handleFlyoutLeave = (event) => {
    if (this.isWithinNode(this.megaMenuRef.current, event.relatedTarget)) {
      return;
    }
    this.clearFlyout();
  };

  renderCategoryHeader = (label) => (
    <Link
      to="#"
      onClick={(e) => e.preventDefault()}
      style={CATEGORY_HEADER_STYLE}
    >
      {label}
    </Link>
  );

  renderProductLink = (product) => {
    const isActive =
      this.state.hoveredProduct &&
      this.state.hoveredProduct.id === product.id;

    return (
      <li key={product.id}>
        <Link
          to={product.route}
          className={isActive ? "products-nav-item--active" : ""}
          onMouseEnter={(event) => this.handleProductHover(product, event)}
        >
          {product.name}
        </Link>
      </li>
    );
  };

  renderDesktopFlyout() {
    const { hoveredProduct, previewImageSrc, flyoutTop, flyoutLeft } =
      this.state;

    if (!hoveredProduct) {
      return null;
    }

    return (
      <div
        ref={this.flyoutRef}
        className="products-nav-flyout"
        style={{ top: flyoutTop, left: flyoutLeft }}
        aria-live="polite"
        onMouseLeave={this.handleFlyoutLeave}
      >
        <div key={hoveredProduct.id} className="products-nav-flyout__content">
          <div className="products-nav-flyout__image-wrap">
            <img
              className="products-nav-flyout__image"
              src={previewImageSrc}
              alt={hoveredProduct.name}
              onLoad={this.updateFlyoutPosition}
              onError={this.handlePreviewImageError}
            />
          </div>
          <h4 className="products-nav-flyout__title">{hoveredProduct.name}</h4>
          {/* {hoveredProduct.description ? (
            <p className="products-nav-flyout__description">
              {hoveredProduct.description}
            </p>
          ) : null} */}
        </div>
      </div>
    );
  }

  /** Original four-column mega menu; flyout is separate and does not affect width. */
  renderDesktopMenu() {
    const categories = NAV_PRODUCT_CATEGORIES;

    return (
      <li
        className="menu-icon products-nav-parent"
        ref={this.productsNavRef}
        onMouseLeave={this.handleProductsAreaLeave}
      >
        <Link to="#" onClick={(e) => e.preventDefault()}>
          Products
        </Link>
        <ul
          ref={this.megaMenuRef}
          className="mega-menu"
          style={MEGA_MENU_STYLE}
          onMouseLeave={this.handleMegaMenuLeave}
        >
          <li style={COLUMN_STYLE}>
            {this.renderCategoryHeader(categories[0].label)}
            <ul>
              {categories[0].products.map((product) =>
                this.renderProductLink(product)
              )}
            </ul>
          </li>
          <li style={COLUMN_STYLE}>
            {this.renderCategoryHeader(categories[1].label)}
            <ul>
              {categories[1].products.map((product) =>
                this.renderProductLink(product)
              )}
            </ul>
          </li>
          <li style={COLUMN_STYLE}>
            {this.renderCategoryHeader(categories[2].label)}
            <ul>
              {categories[2].products.map((product) =>
                this.renderProductLink(product)
              )}
            </ul>
          </li>
          <li style={COLUMN_STYLE}>
            {this.renderCategoryHeader(categories[3].label)}
            <ul>
              {categories[3].products.map((product) =>
                this.renderProductLink(product)
              )}
            </ul>
          </li>
        </ul>
        {this.renderDesktopFlyout()}
      </li>
    );
  }

  /** Mobile/tablet: nested sub-menus only (no flyout). */
  renderMobileMenu() {
    const mobileCategoryLabels = {
      "solar-battery": "SOLAR BATTERY",
      "solar-inverter": "SOLAR INVERTER",
      "e-bike-battery": "E-BIKE BATTERY",
      "industrial-solutions": "INDUSTRIAL SOLUTIONS",
    };

    return (
      <li>
        <Link>Products</Link>
        <ul className="sub-menu">
          {NAV_PRODUCT_CATEGORIES.map((category) => (
            <li key={category.id}>
              <Link>
                {mobileCategoryLabels[category.id] ||
                  category.label.toUpperCase()}
              </Link>
              <ul className="sub-menu">
                {category.products.map((product) => (
                  <li key={product.id}>
                    <Link to={product.route}>{product.name}</Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </li>
    );
  }

  render() {
    const { variant = "desktop" } = this.props;

    if (variant === "mobile") {
      return this.renderMobileMenu();
    }

    return this.renderDesktopMenu();
  }
}

export default ProductsMegaMenu;
